/**
 * CratePickerModal — shown when a user clicks "Add" on an AI-discovered
 * similar track. Loads the user's existing crates (via /api/cards/albums),
 * pre-highlights the AI-suggested bucket if it matches one the user has,
 * and lets the user pick which crate the discovered track lands in.
 *
 * Replaces the old "auto-route to whatever the genre voting decides"
 * behavior — which meant most AI discoveries ended up in "Other" because
 * the AI track metadata isn't always rich enough to land in a specific
 * 33-bucket subgenre. The picker fixes this UX hole.
 *
 * Props:
 *   - open:           boolean — modal open state
 *   - onClose:        () => void — fires when user dismisses
 *   - track:          { artist, title, reason } — the discovered track
 *   - suggestedBucket: optional string — AI's best guess bucket (pre-highlighted)
 *   - onConfirm:      (bucket) => void — fires with chosen bucket name
 */
import { useEffect, useState } from 'react';
import { ListPlus, Loader2, AlertCircle, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import api from '@/lib/api';

export default function CratePickerModal({ open, onClose, track, suggestedBucket, onConfirm }) {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);

  // Load the user's existing crates when the modal opens.
  useEffect(() => {
    if (!open) {
      // Reset state on close so the next open is fresh
      setAlbums([]);
      setError(null);
      setSelected(null);
      return;
    }
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get('/cards/albums');
        if (!active) return;
        const list = data?.albums || [];
        setAlbums(list);
        // Pre-select the AI-suggested bucket if it exists in the user's
        // crates. Otherwise leave nothing selected — user picks manually.
        if (suggestedBucket && list.some((a) => a.genre_bucket === suggestedBucket)) {
          setSelected(suggestedBucket);
        } else if (list.length > 0) {
          // Fall back to the first crate so there's always a default
          setSelected(list[0].genre_bucket);
        }
      } catch (e) {
        if (active) setError(e?.response?.data?.detail || 'Failed to load your crates');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, suggestedBucket]);

  const handleConfirm = () => {
    if (!selected) return;
    onConfirm?.(selected);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-md sm:max-w-lg w-[95vw] bg-card border-border max-h-[85vh] overflow-y-auto"
        data-testid="crate-picker-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-lg">
            <ListPlus className="w-5 h-5 text-[hsl(var(--primary))]" />
            Pick a crate
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {track ? (
              <>
                Adding <span className="text-foreground/80 font-medium">{track.title}</span>
                {track.artist ? <> by <span className="text-foreground/80 font-medium">{track.artist}</span></> : null}
              </>
            ) : (
              'Choose where this track lands.'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 space-y-2">
          {loading ? (
            <>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={`crate-pick-skel-${i}`} className="h-12 rounded-xl" />
              ))}
            </>
          ) : error ? (
            <div className="rounded-xl border border-dashed border-border bg-background p-6 text-center">
              <AlertCircle className="w-7 h-7 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm text-muted-foreground">{error}</div>
            </div>
          ) : albums.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-background p-6 text-center">
              <ListPlus className="w-7 h-7 mx-auto text-muted-foreground mb-2" />
              <div className="text-sm text-muted-foreground">
                You don&apos;t have any crates yet. Scan a few snapshots first, then come back to add discoveries.
              </div>
            </div>
          ) : (
            <ul
              className="space-y-1.5 max-h-[50vh] overflow-y-auto pr-1"
              data-testid="crate-picker-list"
              role="radiogroup"
              aria-label="Pick a crate"
            >
              {albums.map((a) => {
                const isSelected = selected === a.genre_bucket;
                const isSuggested = suggestedBucket && a.genre_bucket === suggestedBucket;
                return (
                  <li key={a.genre_bucket}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      onClick={() => setSelected(a.genre_bucket)}
                      data-testid={`crate-picker-option-${a.genre_bucket}`}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] ${
                        isSelected
                          ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10'
                          : 'border-border bg-background hover:border-[hsl(var(--primary))]/40 hover:bg-secondary/40'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 grid place-items-center shrink-0 ${
                          isSelected
                            ? 'border-[hsl(var(--primary))] bg-[hsl(var(--primary))]'
                            : 'border-border bg-background'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && <Check className="w-3 h-3 text-[hsl(var(--primary-foreground))]" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-semibold truncate text-sm">
                          {a.genre_bucket}
                        </div>
                        <div className="text-[11px] text-muted-foreground font-mono">
                          {a.count} {a.count === 1 ? 'track' : 'tracks'}
                        </div>
                      </div>
                      {isSuggested && (
                        <span
                          className="text-[10px] uppercase font-mono px-1.5 py-0.5 rounded-md bg-[var(--brand-lime)]/15 text-[var(--brand-lime)] border border-[var(--brand-lime)]/30"
                          data-testid="crate-picker-suggested-badge"
                        >
                          Suggested
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            className="rounded-lg"
            data-testid="crate-picker-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !selected}
            className="rounded-lg"
            data-testid="crate-picker-confirm"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ListPlus className="w-4 h-4 mr-2" />}
            Add to crate
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
